import type { DealMeasurementBindingV1, ValueBasis } from './ma-deal-case';

export type FourSeriesEvidenceBasis =
  | 'observed'
  | 'attested'
  | 'modeled'
  | 'unverified';

export const FOUR_SERIES_EVIDENCE_BASIS_VALUES: readonly FourSeriesEvidenceBasis[] = Object.freeze([
  'observed',
  'attested',
  'modeled',
  'unverified',
]);

export function isFourSeriesEvidenceBasis(value: unknown): value is FourSeriesEvidenceBasis {
  return typeof value === 'string' && FOUR_SERIES_EVIDENCE_BASIS_VALUES.includes(value as FourSeriesEvidenceBasis);
}

export type FourSeriesDeliveryState =
  | 'ahead'
  | 'on_track'
  | 'watch'
  | 'off_track'
  | 'not_measurable';

export type FourSeriesPoint = Readonly<{
  asOf: string;
  value: number;
}>;

export type FourSeriesComparableIdentity = Readonly<
  Pick<DealMeasurementBindingV1, 'metricKey' | 'unit'> & {
    valueBasis: ValueBasis;
  }
>;

export type FourSeriesSeriesSet<Point> = Readonly<{
  originalPromise: readonly Point[];
  currentPlan: readonly Point[];
  actual: readonly Point[];
  forecast: readonly Point[];
}>;

export type FourSeriesComparableEnvelope<Point> = Readonly<{
  comparisonBasis: FourSeriesComparableIdentity;
  series: FourSeriesSeriesSet<Point>;
}>;

export type FourSeriesMilestoneState = Readonly<{
  status: 'on_track' | 'slipped';
  asOf: string;
}> | null;

export type FourSeriesThresholds = Readonly<{
  onTrackBehindFraction: number;
  watchBehindFraction: number;
}>;

export const DEFAULT_FOUR_SERIES_THRESHOLDS: FourSeriesThresholds = Object.freeze({
  onTrackBehindFraction: 0.05,
  watchBehindFraction: 0.15,
});

export type FourSeriesAssessmentReason =
  | 'as_of_invalid'
  | 'non_comparable'
  | 'milestone_as_of_invalid'
  | 'promise_missing_as_of'
  | 'plan_missing_as_of'
  | 'actual_missing_as_of'
  | 'promise_zero_as_of'
  | 'thresholds_invalid'
  | 'ahead_of_promise_curve'
  | 'on_promise_curve'
  | 'behind_on_track_band'
  | 'behind_on_watch_band'
  | 'behind_beyond_off_track_threshold'
  | 'milestone_slipped'
  | 'forecast_missing'
  | 'forecast_horizon_mismatch';

export type FourSeriesAssessmentInput = Readonly<FourSeriesComparableEnvelope<FourSeriesPoint> & {
  asOf: string;
  milestoneState: FourSeriesMilestoneState;
  evidenceBasis: FourSeriesEvidenceBasis;
  thresholds: FourSeriesThresholds;
}>;

export type FourSeriesAssessment = Readonly<{
  currentPlanToDate: number | null;
  /** Selected-date alignment only; not freshness or reporting-window equivalence. */
  comparability?: Readonly<{ verdict: 'aligned' | 'drifted'; maxDateSpreadDays: number }>;
  selectedAsOf?: Readonly<{ originalPromise: string; currentPlan: string; actual: string }>;
  varianceToDate: number | null;
  varianceToPlan: number | null;
  forecastEndpointVariance: number | null;
  deliveryState: FourSeriesDeliveryState;
  evidenceBasis: FourSeriesEvidenceBasis;
  reasons: readonly FourSeriesAssessmentReason[];
}>;

/**
 * Assesses four separately governed series. It aligns the promise, plan and actual at the
 * supplied as-of date and never fills a missing series or manufactures a forecast.
 */
export function assessFourSeries(input: FourSeriesAssessmentInput): FourSeriesAssessment {
  if (!comparableIdentityIsValid(input.comparisonBasis)) return notMeasurable('non_comparable');
  const asOfTime = strictTimestamp(input.asOf);
  if (asOfTime === null) return notMeasurable('as_of_invalid');
  const milestoneTime = input.milestoneState === null
    ? null
    : strictTimestamp(input.milestoneState.asOf);
  if (input.milestoneState !== null && milestoneTime === null) {
    return notMeasurable('milestone_as_of_invalid');
  }
  const promiseToDate = latestAtOrBefore(input.series.originalPromise, asOfTime);
  if (!promiseToDate) return notMeasurable('promise_missing_as_of');
  const planToDate = latestAtOrBefore(input.series.currentPlan, asOfTime);
  if (!planToDate) return notMeasurable('plan_missing_as_of');
  const actualToDate = latestAtOrBefore(input.series.actual, asOfTime);
  if (!actualToDate) return notMeasurable('actual_missing_as_of');
  if (promiseToDate.value === 0) return notMeasurable('promise_zero_as_of');
  if (!thresholdsAreValid(input.thresholds)) return notMeasurable('thresholds_invalid');

  const varianceToDate = actualToDate.value - promiseToDate.value;
  const varianceToPlan = actualToDate.value - planToDate.value;
  const behindFraction = Math.max(0, -varianceToDate / Math.abs(promiseToDate.value));
  const quantitative = quantitativeState(varianceToDate, behindFraction, input.thresholds);
  const milestoneSlipped =
    input.milestoneState?.status === 'slipped' &&
    milestoneTime !== null && milestoneTime <= asOfTime;
  const deliveryState = milestoneSlipped && (quantitative.state === 'ahead' || quantitative.state === 'on_track')
    ? 'watch'
    : quantitative.state;
  const reasons: FourSeriesAssessmentReason[] = [quantitative.reason];
  if (milestoneSlipped) reasons.push('milestone_slipped');
  const forecastEndpointVariance = endpointVariance(input.series.originalPromise, input.series.forecast);
  if (forecastEndpointVariance === null) {
    reasons.push(input.series.forecast.length ? 'forecast_horizon_mismatch' : 'forecast_missing');
  }

  const selectedAsOf = { originalPromise: promiseToDate.asOf, currentPlan: planToDate.asOf, actual: actualToDate.asOf };
  return {
    selectedAsOf,
    comparability: selectedDateComparability(selectedAsOf),
    currentPlanToDate: planToDate.value,
    varianceToDate,
    varianceToPlan,
    forecastEndpointVariance,
    deliveryState,
    evidenceBasis: input.evidenceBasis,
    reasons,
  };
}

function selectedDateComparability(dates: NonNullable<FourSeriesAssessment['selectedAsOf']>): NonNullable<FourSeriesAssessment['comparability']> {
  // These selected timestamps already passed strictTimestamp at the selection seam.
  const times = Object.values(dates).map((date) => Date.parse(date));
  const maxDateSpreadDays = (Math.max(...times) - Math.min(...times)) / 86_400_000;
  return { verdict: maxDateSpreadDays === 0 ? 'aligned' : 'drifted', maxDateSpreadDays };
}

function comparableIdentityIsValid(value: unknown): value is FourSeriesComparableIdentity {
  if (!value || typeof value !== 'object') return false;
  const identity = value as Record<string, unknown>;
  return typeof identity.metricKey === 'string'
    && identity.metricKey.trim().length > 0
    && typeof identity.unit === 'string'
    && identity.unit.trim().length > 0
    && (identity.valueBasis === 'run_rate'
      || identity.valueBasis === 'in_period'
      || identity.valueBasis === 'cumulative'
      || identity.valueBasis === 'one_time');
}

function latestAtOrBefore(series: readonly FourSeriesPoint[], boundary: number): FourSeriesPoint | null {
  return series.reduce<FourSeriesPoint | null>((latest, point) => {
    const pointTime = strictTimestamp(point.asOf);
    if (!Number.isFinite(point.value) || pointTime === null || pointTime > boundary) return latest;
    const latestTime = latest ? strictTimestamp(latest.asOf) : null;
    return latestTime === null || pointTime > latestTime ? point : latest;
  }, null);
}

function endpointVariance(promise: readonly FourSeriesPoint[], forecast: readonly FourSeriesPoint[]): number | null {
  const promiseEndpoint = latestAtOrBefore(promise, Infinity);
  if (!promiseEndpoint) return null;
  const horizon = strictTimestamp(promiseEndpoint.asOf);
  if (horizon === null) return null;
  const forecastAtHorizon = latestAtOrBefore(forecast, horizon);
  return forecastAtHorizon && strictTimestamp(forecastAtHorizon.asOf) === horizon
    ? forecastAtHorizon.value - promiseEndpoint.value
    : null;
}

function thresholdsAreValid(thresholds: FourSeriesThresholds): boolean {
  return Number.isFinite(thresholds.onTrackBehindFraction) &&
    Number.isFinite(thresholds.watchBehindFraction) &&
    thresholds.onTrackBehindFraction >= 0 &&
    thresholds.watchBehindFraction >= thresholds.onTrackBehindFraction;
}

function quantitativeState(
  variance: number,
  behindFraction: number,
  thresholds: FourSeriesThresholds,
): Readonly<{ state: Exclude<FourSeriesDeliveryState, 'not_measurable'>; reason: FourSeriesAssessmentReason }> {
  if (variance > 0) return { state: 'ahead', reason: 'ahead_of_promise_curve' };
  if (variance === 0) return { state: 'on_track', reason: 'on_promise_curve' };
  if (behindFraction <= thresholds.onTrackBehindFraction) return { state: 'on_track', reason: 'behind_on_track_band' };
  if (behindFraction <= thresholds.watchBehindFraction) return { state: 'watch', reason: 'behind_on_watch_band' };
  return { state: 'off_track', reason: 'behind_beyond_off_track_threshold' };
}

function notMeasurable(reason: FourSeriesAssessmentReason): FourSeriesAssessment {
  return {
    currentPlanToDate: null,
    varianceToDate: null,
    varianceToPlan: null,
    forecastEndpointVariance: null,
    deliveryState: 'not_measurable',
    evidenceBasis: 'unverified',
    reasons: [reason],
  };
}

function strictTimestamp(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?Z)?$/.exec(value);
  if (!match) return null;
  const parsed = Date.parse(value.length === 10 ? `${value}T00:00:00.000Z` : value);
  if (!Number.isFinite(parsed)) return null;
  const date = new Date(parsed);
  const expected = [
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4] ?? 0),
    Number(match[5] ?? 0),
    Number(match[6] ?? 0),
  ];
  const actual = [
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  ];
  return expected.every((part, index) => part === actual[index]) ? parsed : null;
}
